import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
} from '@mui/material';
import {
  Dashboard,
  People,
  Settings,
  Person,
  School,
  HealthAndSafety,
  Flag,
  Badge as BadgeIcon,
  VerifiedUser,
  LocationCity,
  ToggleOn,
  Map as MapIcon,
  Star as StarIcon,
  ReportProblem,
  PrivacyTip,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { BrandLockup } from './BrandLockup';

interface AdminSidebarProps {
  open: boolean;
  onClose: () => void;
}

export const AdminSidebar: React.FC<AdminSidebarProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    {
      text: 'Dashboard',
      icon: <Dashboard />,
      path: '/admin',
    },
    {
      text: 'User Management',
      icon: <People />,
      path: '/admin/users',
    },
    {
      text: 'Training Management',
      icon: <School />,
      path: '/admin/training',
    },
    {
      text: 'Safety Contacts',
      icon: <HealthAndSafety />,
      path: '/admin/safety-contacts',
    },
    {
      text: 'Moderation Queue',
      icon: <Flag />,
      path: '/admin/moderation',
    },
    {
      text: 'Credential Review',
      icon: <BadgeIcon />,
      path: '/admin/credential-review',
    },
    {
      text: 'Incident Response',
      icon: <ReportProblem />,
      path: '/admin/incidents',
    },
    {
      text: 'Data Access Queue',
      icon: <PrivacyTip />,
      path: '/admin/data-access-requests',
    },
    {
      text: 'Caregiver Verification',
      icon: <VerifiedUser />,
      path: '/admin/caregiver-verification',
    },
    {
      text: 'State Agency Mappings',
      icon: <LocationCity />,
      path: '/admin/state-agency-mappings',
    },
    {
      text: 'Service Areas',
      icon: <MapIcon />,
      path: '/admin/service-areas',
    },
    {
      text: 'Premium Placements',
      icon: <StarIcon />,
      path: '/admin/premium-placements',
    },
    {
      text: 'Feature Flags',
      icon: <ToggleOn />,
      path: '/admin/feature-flags',
    },
    {
      text: 'Admin Profile',
      icon: <Person />,
      path: '/admin/profile',
    },
    {
      text: 'Settings',
      icon: <Settings />,
      path: '/admin/settings',
    },
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 280,
          boxSizing: 'border-box',
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <BrandLockup height={28} suffix="Admin" />
        </Box>
        <Divider />
      </Box>
      
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              onClick={() => handleNavigation(item.path)}
              selected={isActive(item.path)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: isActive(item.path) ? 'primary.main' : 'inherit',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text}
                sx={{
                  '& .MuiListItemText-primary': {
                    color: isActive(item.path) ? 'primary.main' : 'inherit',
                    fontWeight: isActive(item.path) ? 600 : 400,
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Drawer>
  );
};
